import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TitlesCollectionResponse } from './entities/titles-collection-response';
import { GenericCode } from './entities/generic-code';
import { GenderCollectionResponse } from './entities/gender-collection-response';
import { LanguagesCollectionResponse } from './entities/languages-collection-response';
import { RacesCollectionResponse } from './entities/races-collection-response';
import { PlacesOfServiceCollectionResponse } from './entities/places-of-service-collection-response';
import { PlaceOfService } from './entities/place-of-service';
import { VatRate } from './entities/vat-rate';
import { VatRateItemResponse } from './entities/vat-rate-item-response';
import { TariffCode } from './entities/tariff-code';
import { DiagnosisCode } from './entities/diagnosis-code';
import { DiagnosisCodeCollectionResponse } from './entities/diagnosis-code-collection-response';
import { TariffCodeCollectionResponse } from './entities/tariff-code-collection-response';
import { ApiService } from '../base/api.service';
import { DatePipe } from '@angular/common';
import { MasterPermission } from './entities/master-permission';
import { MasterPermissionCollectionResponse } from './entities/master-permission-collection-response';
import { MasterSystemReportType } from '../../report-builder/entities/master-system-report-type';
import { MasterSystemReportTypesCollectionResponse } from './entities/master-system-report-types-collection-response';
import { InsertableValueCollectionResponse } from './entities/insertable-value-collection-response';
import { InsertableValue } from '../../entities/base/insertable-value';
import { InsertableValueEntity } from '../../entities/base/insertable-value-entity';

/**
 * MasterDataRepositoryService
 *
 * Stateless repository for master/reference data API calls.
 * Contains ONLY API call wrappers - no state management.
 *
 * State management is handled by MasterDataStore.
 */
@Injectable({
  providedIn: 'root'
})
export class MasterDataRepositoryService {
  private api = inject(ApiService);
  private datePipe = inject(DatePipe);

  /**
   * Get patient genders
   * @returns Observable with array of gender codes
   */
  genders(): Observable<GenericCode[]> {
    return this.api.get<GenderCollectionResponse>('masterdata/patient-genders')
      .pipe(map(response => response.items));
  }

  /**
   * Get patient titles
   * @returns Observable with array of title codes
   */
  titles(): Observable<GenericCode[]> {
    return this.api.get<TitlesCollectionResponse>('masterdata/patient-titles')
      .pipe(map(response => response.items));
  }

  /**
   * Get patient races
   * @returns Observable with array of race codes
   */
  races(): Observable<GenericCode[]> {
    return this.api.get<RacesCollectionResponse>('masterdata/patient-races')
      .pipe(map(response => response.items));
  }

  /**
   * Get patient languages
   * @returns Observable with array of language codes
   */
  languages(): Observable<GenericCode[]> {
    return this.api.get<LanguagesCollectionResponse>('masterdata/patient-languages')
      .pipe(map(response => response.items));
  }

  /**
   * Get places of service
   * @returns Observable with array of place of service
   */
  placesOfService(): Observable<PlaceOfService[]> {
    return this.api.get<PlacesOfServiceCollectionResponse>('masterdata/places-of-service')
      .pipe(map(response => response.items));
  }

  /**
   * Get VAT rate for a specific date
   * @param date - Date to query VAT rate for (defaults to current date)
   * @returns Observable with VAT rate
   */
  vatRate(date: Date | string = new Date()): Observable<VatRate> {
    if (date instanceof Date) {
      date = this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss');
    }
    return this.api.get<VatRateItemResponse>('masterdata/vat-rate', { 'queryDate': date })
      .pipe(map(response => response.value));
  }

  /**
   * Search for tariff codes
   * @param query - Search query string
   * @param discipline - Discipline code
   * @param isSpecialist - Whether the user is a specialist
   * @returns Observable with array of tariff codes
   */
  searchTariffCodes(query: string, discipline: string, isSpecialist: boolean): Observable<TariffCode[]> {
    return this.api.get<TariffCodeCollectionResponse>('masterdata/tariff-codes/search/', {
      'query': query,
      'disciplineCode': discipline,
      'isSpecialist': isSpecialist
    }).pipe(map(response => response.items));
  }

  /**
   * Search for diagnosis codes
   * @param query - Search query string
   * @returns Observable with array of diagnosis codes
   */
  searchDiagnosisCodes(query: string): Observable<DiagnosisCode[]> {
    return this.api.get<DiagnosisCodeCollectionResponse>('masterdata/diagnosis-codes/search/', { 'query': query })
      .pipe(map(response => response.items));
  }

  /**
   * Get all master permissions
   * @returns Observable with array of permissions
   */
  permissions(): Observable<MasterPermission[]> {
    return this.api.get<MasterPermissionCollectionResponse>('masterdata/permissions')
      .pipe(map(response => response.items));
  }

  /**
   * Get master system report types
   * @returns Observable with array of system report types
   */
  systemReportTypes(): Observable<MasterSystemReportType[]> {
    return this.api.get<MasterSystemReportTypesCollectionResponse>('masterdata/system-report-templates')
      .pipe(map(response => response.items));
  }

  insertableValues(entity: InsertableValueEntity): Observable<InsertableValue[]> {
    return this.api.get<InsertableValueCollectionResponse>(`masterdata/insertable-values/${entity}`)
      .pipe(map(response => response.items));
  }
}
